import {
  Button,
  SidePanel,
  SidePanelActions,
  Typography,
  Checkbox,
  TextField,
  Autocomplete,
  Option
} from '@getgo/chameleon-web-react-wrapper/register';
import { ColumnFilter, ColumnFiltersState } from '@tanstack/react-table';
import { ChangeEvent, ReactElement, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './JobHistoryFilterPanel.module.scss';
import { AutocompleteComponent, CheckboxComponent, TextFieldComponent } from '@getgo/chameleon-web';
import Tenant from '../../../core/models/Tenant';

interface JobHistoryFilterPanelProps {
  isOpen: boolean;
  filtersState: ColumnFiltersState;
  onClose?: () => void;
  onFiltersChange?: (filters: ColumnFiltersState) => void;
  setSelectedTenants: (tenants: Tenant[]) => void;
  autocompleteRef: RefObject<AutocompleteComponent>;
  globalTenants: Tenant[];
}

export default function JobHistoryFilterPanel({
  isOpen,
  filtersState,
  onClose,
  onFiltersChange,
  setSelectedTenants,
  autocompleteRef,
  globalTenants
}: JobHistoryFilterPanelProps): ReactElement {
  const getValue = <T,>(id: string): T | null => (filtersState.find(f => f.id === id)?.value as T) ?? null;

  const { t } = useTranslation();
  const [name, setName] = useState(getValue<string>('displayName') ?? '');
  const [policyName, setPolicyName] = useState(getValue<string>('policyName') ?? '');
  const [detailedStatus, setDetailedStatus] = useState([...(getValue<string>('detailedStatus') ?? [])]);
  const [updates, setUpdates] = useState([...(getValue<string>('updateCount') ?? [])]);
  const [deviceCount] = useState(getValue<string>('deviceCount') ?? '0');
  const [tenantFilters, setTenantFilters] = useState([...(getValue<string>('tenants') ?? [])]);

  useEffect(() => {
    setName(getValue<string>('displayName') ?? '');
    setPolicyName(getValue<string>('policyName') ?? '');
    setDetailedStatus([...(getValue<string>('detailedStatus') ?? [])]);
    setUpdates([...(getValue<string>('updateCount') ?? [])]);
    setTenantFilters([...(getValue<string>('tenants') ?? [])]);
  }, [filtersState]);

  function mapToFilterState(): ColumnFiltersState {
    const deduplicate = (filter: ColumnFilter): ColumnFilter => ({
      id: filter.id,
      value: [...new Set(filter.value as string[])]
    });

    return [
      name ? { id: 'displayName', value: name } : null,
      policyName ? { id: 'policyName', value: policyName } : null,
      detailedStatus.length > 0 ? { id: 'detailedStatus', value: detailedStatus } : null,
      updates.length > 0 ? { id: 'updateCount', value: updates } : null,
      deviceCount !== '0' ? { id: 'deviceCount', value: deviceCount } : null,
      tenantFilters.length > 0 ? { id: 'tenants', value: tenantFilters } : null
    ].flatMap(f => {
      if (!f) return [];
      return Array.isArray(f.value) ? deduplicate(f) : f;
    });
  }

  function checkboxHandlerFactory(state: string[], setter: (value: string[]) => void) {
    return (item: string) => (e: ChangeEvent<CheckboxComponent>) => {
      if (e.target.checked) {
        setter([...state, item]);
      } else {
        setter(state.filter(s => s !== item));
      }
    };
  }

  const onDetailedStatusChange = checkboxHandlerFactory(detailedStatus, setDetailedStatus);
  const onUpdatesChange = checkboxHandlerFactory(updates, setUpdates);

  return (
    <SidePanel
      isOpen={isOpen}
      closeLabel="Close"
      onClose={onClose}
      headingStart={<Typography variant="heading-small">{t('jobHistoryGrid.title')}</Typography>}
      footer={
        <SidePanelActions>
          <Button
            variant="neutral"
            onClick={() => {
              autocompleteRef.current?.clear();
              onFiltersChange?.([]);
            }}>
            {t('jobHistoryGrid.clearFilter')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setSelectedTenants(
                (autocompleteRef.current?.selectedOptions
                  .map(option => {
                    return globalTenants.find(tenant => option.dataset.optionId === tenant.uuid);
                  })
                  .filter(tenant => tenant !== undefined) as Tenant[]) ?? []
              );
              onFiltersChange?.(mapToFilterState());
            }}>
            {t('jobHistoryGrid.applyFilter')}
          </Button>
        </SidePanelActions>
      }>
      <div className={styles.sidepanelContents}>
        <Typography className={styles.filterCaption} variant="caption-medium-strong">
          {t('jobHistoryGrid.headers.displayName')}
        </Typography>
        <TextField
          placeholder="Search"
          value={name}
          onChange={(e: ChangeEvent<TextFieldComponent>) => setName(e.target.value)}
          labelId="search-name"></TextField>
        <Typography className={styles.filterCaption} variant="caption-medium-strong">
          {t('jobHistoryGrid.headers.detailedStatus')}
        </Typography>
        <Checkbox onChange={onDetailedStatusChange('Scheduled')} checked={detailedStatus.includes('Scheduled')}>
          {t('jobHistoryGrid.groups.scheduled')}
        </Checkbox>
        <Checkbox onChange={onDetailedStatusChange('Running')} checked={detailedStatus.includes('Running')}>
          {t('jobHistoryGrid.groups.running')}
        </Checkbox>
        <Checkbox onChange={onDetailedStatusChange('Successful')} checked={detailedStatus.includes('Successful')}>
          {t('jobHistoryGrid.groups.successful')}
        </Checkbox>
        <Checkbox
          onChange={onDetailedStatusChange('PartiallySuccessful')}
          checked={detailedStatus.includes('PartiallySuccessful')}>
          {t('jobHistoryGrid.groups.partiallySuccessful')}
        </Checkbox>
        <Checkbox onChange={onDetailedStatusChange('Failed')} checked={detailedStatus.includes('Failed')}>
          {t('jobHistoryGrid.groups.failed')}
        </Checkbox>
        <Checkbox onChange={onDetailedStatusChange('Cancelled')} checked={detailedStatus.includes('Cancelled')}>
          {t('jobHistoryGrid.groups.cancelled')}
        </Checkbox>
        <Typography className={styles.filterCaption} variant="caption-medium-strong">
          {t('jobHistoryGrid.headers.updateCount')}
        </Typography>
        <Checkbox onChange={onUpdatesChange('os')} checked={updates.includes('os')}>
          {t('jobHistoryGrid.updates.os')}
        </Checkbox>
        <Checkbox onChange={onUpdatesChange('app')} checked={updates.includes('app')}>
          {t('jobHistoryGrid.updates.app')}
        </Checkbox>
        {globalTenants.length >= 2 && (
          <Autocomplete
            ref={autocompleteRef}
            className={styles.filterCaption}
            multiple={true}
            label={t('jobHistoryGrid.headers.tenants')}
            aria-label="tenants"
            clearButtonLabel="clear"
            toggleButtonLabel="toggle"
            placeholder="Select tenants"
            onChange={event => {
              setTenantFilters(
                event.detail.selectedOptions.map(
                  option =>
                    globalTenants.find(
                      (tenant: any) => tenant.uuid === option.dataset.optionId || tenant.id === option.dataset.optionId
                    )?.name ?? ''
                )
              );
            }}>
            {globalTenants.map(tenant => {
              return (
                <Option
                  key={tenant.uuid}
                  label={tenant.name}
                  id={tenant.uuid}
                  data-option-id={tenant.uuid}
                  value={tenant.name}
                  aria-label={tenant.name}>
                  <Typography>{tenant.name}</Typography>
                </Option>
              );
            })}
          </Autocomplete>
        )}
      </div>
    </SidePanel>
  );
}
